import type { VoiceBackground } from './types';
import { cn } from '../cn';
import Button from '../Input2/Button';
import ButtonSegment from '../Input2/ButtonSegment';
import { VOICE_COLOR, voiceMutedTextClassName } from './voiceModeStyles';

const OPTIONS: { id: VoiceBackground; label: string; tooltip: string }[] = [
  { id: 'off', label: 'Off', tooltip: 'Just your voice' },
  { id: 'office', label: 'Office', tooltip: 'Quiet office clatter under your voice' },
  { id: 'office_ring', label: 'Office + phone', tooltip: 'Office clatter and a desk phone ringing a few desks away' },
];

export type VoiceBackgroundControlProps = {
  value: VoiceBackground;
  onChange: (background: VoiceBackground) => void;
  disabled?: boolean;
  className?: string;
};

/**
 * "Background: Off / Office / Office + phone" for a generated voice note. Shared by every Voice
 * Drop composer (Take Action, the DFY contact page, the voice-note card the extension renders) so
 * the choice looks and behaves the same everywhere. The caller owns the value (the rep's
 * remembered default) and decides what a change does — usually regenerate the audio.
 */
export function VoiceBackgroundControl({ value, onChange, disabled, className }: VoiceBackgroundControlProps) {
  return (
    <div className={cn('flex items-center gap-2', className)} data-testid="voice-background-control">
      <span className={cn('text-xs font-medium', voiceMutedTextClassName)}>Background</span>
      <ButtonSegment<VoiceBackground>
        size="xs"
        color={VOICE_COLOR}
        tone="outline"
        selectedId={value}
        disabled={disabled}
        onChange={(next) => {
          if (next !== value) onChange(next);
        }}>
        {OPTIONS.map((option) => (
          <Button key={option.id} id={option.id} tooltip={option.tooltip} data-testid={`voice-background-${option.id}`}>
            {option.label}
          </Button>
        ))}
      </ButtonSegment>
    </div>
  );
}
